import { InspectionTypeName } from './types';

// inspectionTypes
export const AUTONOMOUS_DRONE = 'AUTONOMOUS_DRONE';
export const BLADE_EXTERNAL = 'BLADE_EXTERNAL';
export const BLADE_EXTERNAL_OTHER = 'BLADE_EXTERNAL_OTHER';
export const BLADE_INTERNAL = 'BLADE_INTERNAL';
export const BLADE_INTERNAL_LEGACY = 'BLADE_INTERNAL_LEGACY';
export const GEARBOX_BORESCOPE = 'GEARBOX_BORESCOPE';
export const GENERIC = 'GENERIC';
export const GROUND_PHOTO = 'GROUND_PHOTO';
export const MANUAL_DRONE = 'MANUAL_DRONE';
export const ROPE_INSPECTION = 'ROPE_INSPECTION';
export const TOWER_EXTERNAL = 'TOWER_EXTERNAL';
export const TRANSITION_PIECE = 'TRANSITION_PIECE';

export function parseInspectionType(inspectionTypeName?: InspectionTypeName) {
  // This is a hack: we store type names as editable strings in the database, They should probably become
  // constants in code.
  const n = inspectionTypeName;
  if (n && n.toUpperCase && n.toUpperCase().match(/AUTONOMOUS[ _]DRONE$/)) return AUTONOMOUS_DRONE;
  if (n && n.toUpperCase && n.toUpperCase().match(/BLADE[ _]EXTERNAL$/)) return BLADE_EXTERNAL;
  if (n && n.toUpperCase && n.toUpperCase().match(/BLADE[ _]EXTERNAL[ _]OTHER$/))
    return BLADE_EXTERNAL_OTHER;
  if (n && n.toUpperCase && n.toUpperCase().match(/BLADE[ _]INTERNAL$/)) return BLADE_INTERNAL;
  if (n && n.toUpperCase && n.toUpperCase().match(/BLADE[ _]INTERNAL[ _]LEGACY$/))
    return BLADE_INTERNAL_LEGACY;
  if (n && n.toUpperCase && n.toUpperCase().match(/GEARBOX[ _]BORESCOPE$/))
    return GEARBOX_BORESCOPE;
  if (n && n.toUpperCase && n.toUpperCase().match(/GENERIC$/)) return GENERIC;
  if (n && n.toUpperCase && n.toUpperCase().match(/GROUND[ _]PHOTO$/)) return GROUND_PHOTO;
  if (n && n.toUpperCase && n.toUpperCase().match(/MANUAL[ _]DRONE$/)) return MANUAL_DRONE;
  if (n && n.toUpperCase && n.toUpperCase().match(/ROPE[ _]INSPECTION$/)) return ROPE_INSPECTION;
  if (n && n.toUpperCase && n.toUpperCase().match(/TOWER[ _]EXTERNAL$/)) return TOWER_EXTERNAL;
  if (n && n.toUpperCase && n.toUpperCase().match(/TRANSITION[ _]PIECE$/)) return TRANSITION_PIECE;
  // if inspection type is not found, we don't want to assume any specific default - return undefined
  return undefined;
}

// Convert "10_4" to 10.4 or returns undefined
export function parseDistance(text?: string) {
  try {
    if (text) {
      return parseFloat(text.replace('_', '.')) || undefined;
    }
  } catch {
    // should never happen
    return undefined;
  }
}

// turn fullPath => [anythingBeforeExtension, extension], where extension is ".jpg" etc
// or undefined if no final extension
export const splitExt = (path: string): string[] | [string, undefined] => {
  const m = path.match(/^(.*)(\.[^/\\]+)$/);
  if (m) return m.slice(1); // return only captures, skipping whole match
  return [path, undefined];
};

// split a path at path separators
export const splitPath = (path: string) => path.split(/[/\\]/); // divide path at path separators

// given a list of RegExprs and a list of strings,
// return ordered list and undefined entries where no match was found
export const matchParts = (patterns: RegExp[], segments: string[]) => {
  const result = [];
  const indexOfFinalSegment: number = segments.length ? segments.length - 1 : -1;

  // for each RegExp pattern, check if
  // we have a path segment which matches.
  for (const re of patterns) {
    let hasMatch = false;

    segments.forEach((segment, index) => {
      if (segment && re) {
        const matchedValInIterator = segment.match(re);
        if (matchedValInIterator) {
          const matchedVal = matchedValInIterator[0];
          hasMatch = true;

          // check if matchVal is type number
          const parsedDistance = parseDistance(matchedVal);
          const isNumber = typeof parsedDistance === 'number';
          // if type number and match is on the last segment, it is the distance value
          const isDistance = isNumber && index === indexOfFinalSegment;

          // If it's the distance value or a non-number match, add it to the array
          // else do nothing (we don't want the undefined list item in the result)
          if (!isNumber || isDistance) {
            result.push(matchedVal);
          }
        }
      }
    });

    if (!hasMatch) {
      result.push(undefined);
    }
  }

  return result;
};

// match final path compenents against a list of patterns, returning a list of matches
export const matchPath = (patterns: RegExp[], fullPath: string) => {
  const [path, extension] = splitExt(fullPath.toUpperCase());
  if (path) {
    return [...matchParts(patterns, splitPath(path)), extension];
  }
};

export const bladeInternalPatterns = [
  /^([ABC])$/gi, // Capture 1: blade side
  /(ROOT|TRAILING_EDGE|LEADING_EDGE|BETWEEN_WEB_1|BETWEEN_WEB_2)/g, //Capture 2: chamber name
  /(^[0-9_]+)/g, // Capture 3: distance from root is in filename
];

export interface BladeInternalAssetDescriptors {
  bladePosition?: string;
  bladeChamber?: string;
  distance?: number;
  bladeGlobalId?: null;
}

export function parseBladeInternalPath(fullPath: string): BladeInternalAssetDescriptors {
  const tokens = matchPath(bladeInternalPatterns, fullPath || '');
  /* eslint-disable-next-line no-unused-vars */
  if (tokens) {
    const [bladePosition, bladeChamber, encodedDistance, extension] = tokens; // extension may come into play with Cornis
    return {
      bladePosition,
      bladeChamber,
      distance: parseDistance(encodedDistance),
    };
  }

  return {
    bladePosition: undefined,
    bladeChamber: undefined,
    distance: undefined,
  };
}

export const bladeExternalPatterns = [
  /^([ABC])$/gi, // Capture 1: blade side
  /(TRAILING_EDGE|LEADING_EDGE|SUCTION_SIDE|PRESSURE_SIDE)/g, //Capture 2: chamber name
  /(^[0-9_]+)/g, // Capture 3: distance from root is in filename
];

export interface BladeExternalAssetDescriptors {
  bladePosition?: string;
  bladeSide?: string;
  distance?: number;
  bladeGlobalId?: null;
}

export function parseBladeExternalPath(fullPath?: string): BladeExternalAssetDescriptors {
  const tokens = matchPath(bladeExternalPatterns, fullPath || '');
  /* eslint-disable-next-line no-unused-vars */
  if (tokens) {
    const [bladePosition, bladeSide, encodedDistance, extension] = tokens; // extension may come into play with Cornis
    return {
      bladePosition,
      bladeSide,
      distance: parseDistance(encodedDistance),
    };
  }

  return {
    bladePosition: undefined,
    bladeSide: undefined,
    distance: undefined,
  };
}

export const towerExternalPatterns = [
  /\b(NORTH|SOUTH|EAST|WEST)\b/gi, // Capture 1: tower direction
  /(^[0-9_]+)/g, // Capture 2: distance from root is in filename
];

export interface TowerExternalAssetDescriptors {
  towerSide?: string;
  distance?: number;
  bladePosition: undefined;
}

export function parseTowerExternalPath(fullPath: string): TowerExternalAssetDescriptors {
  const tokens = matchPath(towerExternalPatterns, fullPath || '');
  if (tokens) {
    const [towerSide, encodedDistance] = tokens;
    return {
      towerSide: towerSide?.toLowerCase(),
      distance: parseDistance(encodedDistance),
      bladePosition: undefined,
    };
  }

  return {
    towerSide: undefined,
    distance: undefined,
    bladePosition: undefined,
  };
}

export interface TransitionPieceAssetDescriptors {
  transitionPieceSide: string | undefined;
  distance: number | undefined;
  bladePosition: undefined;
}

export function parseTransitionPiecePath(fullPath: string): TransitionPieceAssetDescriptors {
  const tokens = matchPath(towerExternalPatterns, fullPath || '');
  if (tokens) {
    const [transitionPieceSide, encodedDistance] = tokens;
    return {
      transitionPieceSide: transitionPieceSide?.toLowerCase(),
      distance: parseDistance(encodedDistance),
      bladePosition: undefined,
    };
  }

  return {
    transitionPieceSide: undefined,
    distance: undefined,
    bladePosition: undefined,
  };
}

export function parseInspectionUploadPath(path: string, inspectionTypeName?: InspectionTypeName) {
  switch (parseInspectionType(inspectionTypeName)) {
    case AUTONOMOUS_DRONE:
    case BLADE_EXTERNAL:
    case BLADE_EXTERNAL_OTHER:
    case GROUND_PHOTO:
    case MANUAL_DRONE:
    case ROPE_INSPECTION:
      return parseBladeExternalPath(path);
    case BLADE_INTERNAL:
    case BLADE_INTERNAL_LEGACY:
      return parseBladeInternalPath(path);
    case TRANSITION_PIECE:
      return parseTransitionPiecePath(path);
    case TOWER_EXTERNAL:
      return parseTowerExternalPath(path);
    case GEARBOX_BORESCOPE:
    case GENERIC:
    default:
      //if inspection type is not found, we don't want to assume any specific default - return
      return {};
  }
}
